import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import i18n from '../i18n';
import { ArrowLeft, AlertTriangle, CheckCircle2, Languages } from 'lucide-react';

const LANG_LABELS = {
  en: 'English', es: 'Español', de: 'Deutsch', fr: 'Français', nl: 'Nederlands', ar: 'العربية',
};

/**
 * Recursively flattens an i18n resource object into {"path.to.key": value}.
 * Handles the nested bundles used by Find-2-Day (e.g. savedSearches.*).
 */
const flatten = (obj, prefix = '') => {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  Object.entries(obj).forEach(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flatten(v, key));
    } else {
      out[key] = v;
    }
  });
  return out;
};

const AdminTranslations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(null);

  const report = useMemo(() => {
    const resources = i18n.options.resources || {};
    const enKeys = flatten(resources.en?.translation || {});
    const enKeySet = new Set(Object.keys(enKeys));
    const totalKeys = enKeySet.size;

    return Object.keys(resources)
      .filter((lng) => lng !== 'en')
      .map((lng) => {
        const locale = flatten(resources[lng]?.translation || {});
        const missing = [];
        enKeySet.forEach((k) => {
          if (!(k in locale)) missing.push({ key: k, english: enKeys[k] });
        });
        return {
          lng,
          label: LANG_LABELS[lng] || lng.toUpperCase(),
          total: totalKeys,
          missing,
          coveragePct: totalKeys === 0 ? 100 : Math.round(((totalKeys - missing.length) / totalKeys) * 100),
        };
      })
      .sort((a, b) => b.missing.length - a.missing.length);
  }, []);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <p className="text-gray-600">Admin access required.</p>
          <button onClick={() => navigate('/admin/login')} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-translations-page">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-white/20" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Languages className="h-5 w-5" /> Translation Coverage
            </h1>
            <p className="text-xs text-purple-100">Internal tool — lists keys still using the English fallback in each locale.</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {report.map((r) => {
          const isOpen = expanded === r.lng;
          const allGood = r.missing.length === 0;
          return (
            <section key={r.lng} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`translation-card-${r.lng}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : r.lng)}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${allGood ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {allGood ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-gray-900">{r.label}</p>
                  <p className="text-xs text-gray-500">
                    {r.total - r.missing.length} / {r.total} keys translated · <span className={allGood ? 'text-green-700' : 'text-amber-700'}>{r.coveragePct}% coverage</span>
                  </p>
                </div>
                <span className={`text-sm font-bold ${allGood ? 'text-green-700' : 'text-amber-700'}`} data-testid={`missing-count-${r.lng}`}>
                  {r.missing.length} missing
                </span>
              </button>

              {isOpen && r.missing.length > 0 && (
                <div className="border-t border-gray-100 max-h-96 overflow-y-auto" data-testid={`missing-list-${r.lng}`}>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left font-medium text-gray-500 px-4 py-2 w-1/3">Key</th>
                        <th className="text-left font-medium text-gray-500 px-4 py-2">English fallback</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {r.missing.map((m, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 font-mono text-gray-700 align-top">{m.key}</td>
                          <td className="px-4 py-2 text-gray-900">{String(m.english ?? '').slice(0, 160)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </main>
    </div>
  );
};

export default AdminTranslations;
